import React from 'react';
import { RadioButton, Checkbox } from '@xo-union/tk-component-switches';
import { Button } from '@xo-union/tk-component-buttons';
import { DisplayButton } from '@xo-union/tk-ui-links';
import kebabCase from 'lodash/kebabCase';
import { PillDropdownOptions } from './index';

import styles from './styles.scss';

interface DropdownPropsType extends ActionsPropsType {
  children: React.ReactChild | React.ReactChild[];
}

interface OptionsPropsType {
  options: PillDropdownOptions;
}

interface ActionsPropsType {
  primaryButtonText: string;
  primaryButtonAction: () => void;
  primaryButtonDisabled: boolean;
  secondaryButtonText?: string;
  secondaryButtonAction?: () => void;
  secondaryButtonDisabled?: boolean;
  small?: boolean;
}

export const DropdownWithActions = React.forwardRef<HTMLDivElement, DropdownPropsType>(
  (
    {
      children,
      primaryButtonText,
      primaryButtonAction,
      primaryButtonDisabled,
      secondaryButtonText,
      secondaryButtonAction,
      secondaryButtonDisabled,
      small = false,
    }: DropdownPropsType,
    ref
  ): JSX.Element => {
    return (
      <div className={styles.dropdown + (small ? ` ${styles.smallDropdown}` : '')} ref={ref}>
        <div className={styles.dropdownOptions}>{children}</div>
        <DropdownActions
          primaryButtonText={primaryButtonText}
          primaryButtonAction={primaryButtonAction}
          primaryButtonDisabled={primaryButtonDisabled}
          secondaryButtonText={secondaryButtonText}
          secondaryButtonAction={secondaryButtonAction}
          secondaryButtonDisabled={secondaryButtonDisabled}
        />
      </div>
    );
  }
);

export const RadioOptionsList = ({ options }: OptionsPropsType): JSX.Element => {
  const List = React.useMemo(
    () =>
      options.map(({ text, value }) => {
        return (
          <RadioButton key={value} value={value} aria-label={text} data-testid={kebabCase(`${text}-radio-button`)}>
            {text}
          </RadioButton>
        );
      }),
    [options]
  );

  return <ul role="menu">{List}</ul>;
};

export const CheckBoxOptionsList = ({ options }: OptionsPropsType): JSX.Element => {
  const List = React.useMemo(
    () =>
      options.map(({ text, value }) => {
        return (
          <Checkbox key={value} value={value} aria-label={text} data-testid={kebabCase(`${text}-checkbox`)}>
            {text}
          </Checkbox>
        );
      }),
    [options]
  );

  return <ul role="menu">{List}</ul>;
};

export const DropdownActions = ({
  primaryButtonText,
  primaryButtonAction,
  primaryButtonDisabled,
  secondaryButtonText,
  secondaryButtonAction,
  secondaryButtonDisabled,
}: ActionsPropsType) => {
  const hasSecondary = secondaryButtonText && secondaryButtonAction;
  return (
    <div className={styles.actionFooter}>
      {hasSecondary && (
        <DisplayButton
          color="secondary"
          size="md"
          onClick={secondaryButtonAction}
          disabled={secondaryButtonDisabled || false}
          data-testid={kebabCase(`${secondaryButtonText}-dropdown-secondary-cta`)}
        >
          {secondaryButtonText}
        </DisplayButton>
      )}
      <Button
        color="primary"
        size="md"
        onClick={primaryButtonAction}
        disabled={primaryButtonDisabled || false}
        data-testid={kebabCase(`${primaryButtonText}-dropdown-primary-cta`)}
      >
        {primaryButtonText}
      </Button>
    </div>
  );
};
